/**
 * Helpers for rendering a game's two teams. A game has no home/away distinction:
 * `teamIds` lists the two participating teams (order incidental) and scores are
 * keyed by team id. These helpers centralise the id → display-name lookup and
 * score access so components never reach for a privileged "home"/"away" slot.
 */
import type { Game, GameResult } from '@sulis-liiga/common';

/** Display label for a team id on a game (hydrated name, falling back to the id). */
export function teamLabel(game: Pick<Game, 'teamNames'>, teamId: string): string {
  return game.teamNames?.[teamId] ?? teamId;
}

/** The two team ids of a game as a tuple. */
export function teamIdsOf(game: Pick<Game, 'teamIds'>): [string, string] {
  return [game.teamIds[0], game.teamIds[1]];
}

/** The two team display labels of a game, in `teamIds` order. */
export function teamLabelsOf(game: Pick<Game, 'teamIds' | 'teamNames'>): [string, string] {
  return [teamLabel(game, game.teamIds[0]), teamLabel(game, game.teamIds[1])];
}

/** Read a score keyed by team id (games won, points, etc.), defaulting to 0. */
export function scoreFor(map: Record<string, number> | undefined, teamId: string): number {
  return map?.[teamId] ?? 0;
}

/** Games (sets) won by a team in a result, defaulting to 0. */
export function gamesWon(result: Pick<GameResult, 'scores'>, teamId: string): number {
  return scoreFor(result.scores, teamId);
}

/**
 * Per-set point line for a finished game, e.g. "23-21, 21-19", with the first
 * team's points first. Blank sets (all-zero) are skipped. Returns '' if there
 * are no played sets.
 */
export function formatSetScores(
  sets: Array<Record<string, number>> | undefined,
  teamAId: string,
  teamBId: string,
): string {
  if (!sets) return '';
  return sets
    .filter(s => scoreFor(s, teamAId) !== 0 || scoreFor(s, teamBId) !== 0)
    .map(s => `${scoreFor(s, teamAId)}-${scoreFor(s, teamBId)}`)
    .join(', ');
}
